/**
 * DocumentHeader.tsx
 * 書類ヘッダー: 書類番号・種別・ステータスバッジ・主要アクションボタン群。
 * ロジックは DocumentPage に残し、このコンポーネントは表示のみ担当する。
 */
import React from "react";
import { Link } from "react-router-dom";
import { StatusBadge } from "../StatusBadge.js";
import { type DocumentDetail, type CaseDetail } from "../../api.js";
import type { AnyBlock } from "@juro/shared";
import { btnStyle } from "../../App.js";
import { useDocTypeLabel } from "../../hooks/useDocTypeProfession.js";

interface DocumentHeaderProps {
  doc: DocumentDetail;
  caseDetail: CaseDetail | undefined;
  isDraft: boolean;
  isIssued: boolean;
  editing: boolean;
  blocksDraft: AnyBlock[] | null;
  activeBlocks: AnyBlock[] | null;
  savePending: boolean;
  saveAndIssuePending: boolean;
  pdfPreviewLoading: boolean;
  pdfLoading: boolean;
  onCancelEdit: () => void;
  onSave: () => void;
  onSaveAndIssue: () => void;
  onOpenTemplateSettings: () => void;
  onStartEdit: () => void;
  onOpenRevise: () => void;
  onOpenCancel: () => void;
  /** 見積書のみ: 発行後も理由入力なしで新しい版を作って編集を続けられる */
  onQuickRevise?: () => void;
  quickRevisePending?: boolean;
}

export function DocumentHeader({
  doc, caseDetail,
  isDraft, isIssued, editing,
  blocksDraft, activeBlocks,
  savePending, saveAndIssuePending, pdfPreviewLoading, pdfLoading,
  onCancelEdit, onSave, onSaveAndIssue,
  onOpenTemplateSettings,
  onStartEdit, onOpenRevise, onOpenCancel,
  onQuickRevise, quickRevisePending,
}: DocumentHeaderProps) {
  const isEstimate = doc.doc_type === "estimate";
  const docTypeLabel = useDocTypeLabel(doc.doc_type);
  return (
    <>
      {/* パンくず */}
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
        <Link to="/clients" style={{ color: "#0f766e", textDecoration: "none" }}>顧客管理</Link>
        {" › "}
        {caseDetail && (
          <>
            <Link to={`/clients/${caseDetail.client_id}`} style={{ color: "#0f766e", textDecoration: "none" }}>
              {caseDetail.client_name}
            </Link>
            {" › "}
            <Link to={`/cases/${doc.case_id}`} style={{ color: "#0f766e", textDecoration: "none" }}>
              令和{caseDetail.fiscal_year}年 案件
            </Link>
            {" › "}
          </>
        )}
        {docTypeLabel}
      </div>

      {/* ヘッダーカード */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h1 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
              {doc.doc_number ?? "（未発行）"} {docTypeLabel}
            </h1>
            <StatusBadge status={doc.status} />
            {doc.version > 1 && (
              <span style={{ fontSize: 12, background: "#fef9c3", color: "#854d0e", padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>
                v{doc.version}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {isDraft && editing && (
              <>
                {blocksDraft && (
                  <button style={btnStyle("outline")} onClick={onOpenTemplateSettings}>体裁を整える</button>
                )}
                <button style={btnStyle("outline")} onClick={onCancelEdit}>編集をキャンセル</button>
                <button
                  style={btnStyle("outline")}
                  onClick={onSave}
                  disabled={savePending}
                >
                  {savePending ? "保存中…" : "保存"}
                </button>
                <button
                  style={btnStyle("primary")}
                  onClick={() => {
                    const msg = isEstimate
                      ? "発行しますか？発行後も「内容を変更する」から変更できます（新しい版として保存されます）。"
                      : "発行しますか？発行後は編集できません。";
                    if (confirm(msg)) onSaveAndIssue();
                  }}
                  disabled={saveAndIssuePending || pdfPreviewLoading}
                >
                  {saveAndIssuePending || pdfPreviewLoading ? "処理中…" : "PDFを発行"}
                </button>
              </>
            )}
            {isDraft && !editing && (
              <>
                {(doc.blocks_snapshot || activeBlocks) && (
                  <button style={btnStyle("outline")} onClick={onOpenTemplateSettings}>体裁を整える</button>
                )}
                <button style={btnStyle("outline")} onClick={onStartEdit}>再編集</button>
              </>
            )}
            {isIssued && (
              <>
                {isEstimate && onQuickRevise ? (
                  // 見積書は発行後も気軽に変更できる: 理由入力なしで新しい版（draft）を作って編集へ
                  <button style={btnStyle("primary")} onClick={onQuickRevise} disabled={quickRevisePending}>
                    {quickRevisePending ? "作成中…" : "内容を変更する"}
                  </button>
                ) : (
                  <button style={btnStyle("outline")} onClick={onOpenRevise}>修正版を作成</button>
                )}
                <button style={btnStyle("danger")} onClick={onOpenCancel}>取消</button>
              </>
            )}
          </div>
        </div>
        {doc.issued_at && (
          <p style={{ fontSize: 12, color: "#94a3b8", margin: "8px 0 0" }}>
            発行日：{new Date(doc.issued_at).toLocaleDateString("ja-JP")}
            {doc.issued_by_name && `　担当：${doc.issued_by_name}`}
          </p>
        )}
      </div>
    </>
  );
}
